import React, { useState, useEffect, useRef } from 'react';

interface Option {
  value: string;
  label: string;
}

interface StyledSelectProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (e: { target: { name?: string; value: string } }) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  error?: boolean;
}

/**
 * Custom styled select component to match React Select appearance
 */
const StyledSelect: React.FC<StyledSelectProps> = ({ 
  id, 
  name, 
  value, 
  onChange, 
  options, 
  placeholder = 'Select option', 
  className = '', 
  error = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        id={id}
        onClick={() => setIsOpen(!isOpen)}
        className={`block w-full min-h-[48px] px-3 py-2 text-left bg-light-surfaceSecondary dark:bg-secondary-dark border border-light-border-default dark:border-hover-dark rounded-xl shadow-sm transition-all duration-200 hover:border-light-border-interactive hover:shadow-md focus:ring-2 focus:ring-primary-light focus:border-primary-light dark:focus:ring-primary-dark dark:focus:border-primary-dark ${className} ${error ? 'border-red-500' : ''}`}
      >
        <div className="flex items-center justify-between">
          <span className={`text-base ${selectedOption ? 'text-light-text-primary dark:text-white' : 'text-light-text-muted dark:text-gray-400'}`}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg
            className={`w-5 h-5 text-light-text-secondary dark:text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-[100] w-full mt-1 bg-light-surfaceHover dark:bg-gray-700 border border-light-border-default dark:border-gray-600 rounded-lg shadow-md overflow-hidden">
          <ul className="py-1 max-h-60 overflow-auto">
            {options.map((option) => (
              <li
                key={option.value}
                onClick={() => {
                  onChange({ target: { name, value: option.value } });
                  setIsOpen(false);
                }}
                className={`px-3 py-2 cursor-pointer text-sm transition-colors duration-150 ${option.value === value
                  ? 'bg-light-status-info-bg dark:bg-slate-600 text-light-text-primary dark:text-white'
                  : 'text-light-text-primary dark:text-gray-300 hover:bg-light-interactive-hover dark:hover:bg-gray-600'
                  }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default StyledSelect;
